import {AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {
  CountryCalculatorDto, DiagnosticReportFieldModelDto,
  DiagnosticReportMismatchedRecordSummaryModel,
  DiagnosticReportModel,
  DiagnosticReportSelectedRecordInfo,
  DiagnosticReportWorksheetModelDto,
} from '@features/global-tax-calculator/models/diagnostic-report.model';
import {FlexSheetService} from '@features/spreadsheet/services/flex-sheet.service';
import {SpreadsheetCommandFactory} from '@features/spreadsheet/spreadsheet-command';
import {CommandService} from '@core/services/command.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {FlexSheetNavigationService} from '@features/spreadsheet/services/flex-sheet-navigation.service';
import {PlatformQuery} from '@shared/models/platform-query/platform-query';
import {Api} from '@shared/constants/common-field.constants';
import {PlatformQueryService} from '@core/services/platform-query.service';
import {DialogModel} from '@shared/models/dialog.model';
import {DialogService} from '@shared/services/dialog-service';
import { BehaviorSubject, interval, timer } from 'rxjs';
import {filter, take, timeout} from 'rxjs/operators';
import {SnackBarService} from '@core/services/snack-bar.service';


@Component({
    standalone: false,
    selector: 'app-diagnostic-report',
  templateUrl: './diagnostic-report.component.html',
  styleUrl: './diagnostic-report.component.scss'
})
export class DiagnosticReportComponent implements OnInit, OnDestroy, AfterViewInit  {
  @Input() diagnosticReportWorksheetModels: Array<DiagnosticReportWorksheetModelDto> = new Array<DiagnosticReportWorksheetModelDto>();
  @Input() diagnosticReport: DiagnosticReportModel;
  @Input() diagnosticReportSelectedRecordInfo: DiagnosticReportSelectedRecordInfo = new DiagnosticReportSelectedRecordInfo();
  @Input() selectedMismatchedRecordSummary: DiagnosticReportMismatchedRecordSummaryModel = new DiagnosticReportMismatchedRecordSummaryModel();
  @Input() hideDiagnosticReportDescription$: BehaviorSubject<boolean>;
  @Output() selectedMismatchedRecordEvent$: EventEmitter<any> = new EventEmitter();
  @Output() selectedWorksheetNameEvent$: EventEmitter<any> = new EventEmitter();
  @Output() hideDiagnosticReportDescriptionEvent$: EventEmitter<any> = new EventEmitter<any>();

  private queuedMismatchedRecordSummary: DiagnosticReportMismatchedRecordSummaryModel = null;

  isHeaderOpen = true;

  constructor(private flexSheetService: FlexSheetService,
              private platformQueryService: PlatformQueryService,
              private commandService: CommandService,
              private dialogService: DialogService,
              public flexSheetNavigationService: FlexSheetNavigationService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private snackBarService: SnackBarService) {
  }

  ngOnDestroy(): void {

  }

  ngOnInit(): void {
  }
  onCloseHeader() {
    this.hideDiagnosticReportDescriptionEvent$.emit(true);
  }

  onOpenWorkSheetCell(worksheetName: string, rowIndex: number, columnIndex: number, $event: any) {
    $event.stopPropagation();
    const worksheets = this.flexSheetService.getWorksheets();
    const selectedWorksheetIndex = worksheets.findIndex(ws => ws.name === worksheetName);
    if (selectedWorksheetIndex !== -1) {
      this.navigateToWorksheetCell(selectedWorksheetIndex, worksheets[selectedWorksheetIndex].id, rowIndex, columnIndex);
    }
  }

  navigateToWorksheetCell(worksheetIndex: number, worksheetId: string,  rowIndex: number, colIndex: number) {
    this.fuseSplashScreenService.show();

    this.commandService.executePlatformCommand(
        SpreadsheetCommandFactory.NavigateToPaginatedPageByCellCommand(
          this.flexSheetService.getCurrentWorkBookSetId(), worksheetId, rowIndex - 1, colIndex - 1))
      .pipe(take(1))
      .subscribe({
        next: async response => {
          try {
            await this.flexSheetNavigationService.navigateToPaginatedWorksheetPage(
              worksheetIndex,
              Number(response.fieldValues['GridRow']),
              Number(response.fieldValues['GridCol']),
              Number(response.fieldValues['Row']),
              Number(response.fieldValues['Col'])
            );
            this.emitQueuedMismatchedRecord(worksheetId);
          } finally {
            this.fuseSplashScreenService.hide();
          }
        }
      });
  }
  private emitQueuedMismatchedRecord(worksheetId: string): void {
    if (!this.queuedMismatchedRecordSummary) { return; }

    interval(500)
      .pipe(
        filter(() => this.flexSheetService.getCurrentWorksheetId() === worksheetId),
        take(1),
        timeout(3000)
      )
      .subscribe({
        next: () => {
          this.selectedMismatchedRecordEvent$.emit(this.queuedMismatchedRecordSummary);
          this.queuedMismatchedRecordSummary = null;
        },
        error: () => {
          this.snackBarService.showError('Worksheet not found.');
        }
      });
  }



  onAction(mismatchedRecordSummary: DiagnosticReportMismatchedRecordSummaryModel, worksheetName: string, fieldTitle: string, $event) {
    mismatchedRecordSummary.fieldName = fieldTitle;
    this.queuedMismatchedRecordSummary = mismatchedRecordSummary;
    this.onOpenWorkSheetCell(worksheetName, mismatchedRecordSummary.rowIndex, mismatchedRecordSummary.columnIndex, $event);
  }

  ngAfterViewInit(): void {
  }

  scrollIntoSelectedRecord() {
    if (this.diagnosticReportSelectedRecordInfo && this.diagnosticReportSelectedRecordInfo.selectedReportId) {
      setTimeout(() => {
        const element = document.getElementById(this.diagnosticReportSelectedRecordInfo.selectedReportId);
        if (element) {
          element.scrollIntoView({block: 'center', inline: 'nearest'});
          element.focus();
        }
      }, 2000);
    }
  }

  onWorksheetOpen(worksheetName: any) {
    this.resetSelectedRecordInformation(worksheetName);
    if(this.diagnosticReportSelectedRecordInfo.worksheetName !== worksheetName) {
      this.diagnosticReportSelectedRecordInfo = new DiagnosticReportSelectedRecordInfo();
      this.diagnosticReportSelectedRecordInfo.worksheetName = worksheetName;
      this.selectedWorksheetNameEvent$.emit(worksheetName);
    }
    this.fetchWorksheetFields(worksheetName);
  }

  onShowCountryList($event: any) {
    $event.stopPropagation();
    const query = new PlatformQuery();
    query.name = 'GetDiagnosticReportCountriesQuery';
    query.api = Api.Spreadsheet;
    query.fieldValues['ScenarioId'] = this.diagnosticReport.scenarioId;
    query.fieldValues['ScenarioType'] = this.diagnosticReport.scenarioType;
    query.fieldValues['CountryCode'] = this.diagnosticReport.countryCode;

    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response  => {
      if (response && response.fieldValues) {
        const selectedCountryCalculators = response.fieldValues['SelectedCountryCalculators'];
        if(selectedCountryCalculators && selectedCountryCalculators.length > 0) {

          this.displayCountryList(selectedCountryCalculators);
        }
      }
    });
    // todo: show country list
  }

  private displayCountryList(selectedCountryCalculators: Array<CountryCalculatorDto>) {
    const dialogModel: DialogModel = DialogModel.create(
      'Selected Country Calculators',
      'ShowCountryCalculatorsDialogComponent', {countryCalculators: selectedCountryCalculators},
      '600px',
      '700px'
    );
    dialogModel.cancelCaption = '';
    dialogModel.okCaption = 'Close';
    dialogModel.hideCloseButton = true;
    this.dialogService.showOkCancelDialog(dialogModel).subscribe( res => {
    });
  }

  onWorksheetClose(worksheetName: string) {
    this.resetSelectedRecordInformation(worksheetName);
  }

  private resetSelectedRecordInformation(worksheetName: string) {
    const currentSelectedWorksheet = this.diagnosticReportWorksheetModels.find(worksheet => worksheet.worksheetName === worksheetName);
    /*if (currentSelectedWorksheet) {
      currentSelectedWorksheet.diagnosticReportFieldModelsDto = new Array<DiagnosticReportFieldModelDto>();
    }*/
  }

  private fetchWorksheetFields(worksheetName: string) {
    const query = new PlatformQuery();
    query.name = 'GetDiagnosticReportWorksheetDetailsQuery';
    query.api = Api.Spreadsheet;
    query.fieldValues['ScenarioId'] = this.diagnosticReport.scenarioId;
    query.fieldValues['ScenarioType'] = this.diagnosticReport.scenarioType;
    query.fieldValues['CountryCode'] = this.diagnosticReport.countryCode;
    query.fieldValues['WorksheetName'] = worksheetName;

    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response  => {
      if (response && response.fieldValues) {
        const diagnosticReportWorksheetModelDto = response.fieldValues['DiagnosticReportWorksheetModelDtoModel'];
        if (diagnosticReportWorksheetModelDto) {
          const currentSelectedWorksheet = this.diagnosticReportWorksheetModels.find(worksheet => worksheet.worksheetName === worksheetName);
          if (currentSelectedWorksheet) {
            currentSelectedWorksheet.diagnosticReportFieldModelsDto = diagnosticReportWorksheetModelDto.diagnosticReportFieldModelsDto;
          }
        }
      }
    });
  }

  onOpenWorksheetField(worksheetName: string, fieldModel: DiagnosticReportFieldModelDto) {
    if(this.diagnosticReportSelectedRecordInfo.columnIndex !== fieldModel.columnIndex) {
      this.diagnosticReportSelectedRecordInfo.columnIndex = fieldModel.columnIndex;
      this.diagnosticReportSelectedRecordInfo.fieldModelId = fieldModel.id;
      this.diagnosticReportSelectedRecordInfo.selectedReportId = '';
      this.diagnosticReportSelectedRecordInfo.rowIndex = -1;
    }
    const query = new PlatformQuery();
    query.name = 'GetDiagnosticReportFieldDetailsQuery';
    query.api = Api.Spreadsheet;
    query.fieldValues['ScenarioId'] = this.diagnosticReport.scenarioId;
    query.fieldValues['ScenarioType'] = this.diagnosticReport.scenarioType;
    query.fieldValues['CountryCode'] = this.diagnosticReport.countryCode;
    query.fieldValues['WorksheetName'] = worksheetName;
    query.fieldValues['ColumnIndex'] = fieldModel.columnIndex;
    query.fieldValues['RowIndex'] = fieldModel.rowIndex;
    query.fieldValues['WorkbookSetId'] = this.flexSheetService.getWorkbookSetId();

    this.platformQueryService.executeQuery(query).pipe(take(1)).subscribe(response  => {
      if (response && response.fieldValues) {
        const diagnosticReportWorksheetFieldModelDto = response.fieldValues['DiagnosticReportWorksheetFieldModelDtoModel'];
        console.log("diagnosticReportWorksheetFieldModelDto:", diagnosticReportWorksheetFieldModelDto);
        if (diagnosticReportWorksheetFieldModelDto) {
          const currentSelectedWorksheet = this.diagnosticReportWorksheetModels.find(worksheet => worksheet.worksheetName === worksheetName);
          console.log("currentSelectedWorksheet:", currentSelectedWorksheet);
          if (currentSelectedWorksheet) {
            var currentField = currentSelectedWorksheet.diagnosticReportFieldModelsDto.find(o => o.columnIndex === fieldModel.columnIndex && o.rowIndex === fieldModel.rowIndex);
            console.log("mismatched field:", currentField);
            if(currentField) {
              currentField.mismatchedRecordSummaries = diagnosticReportWorksheetFieldModelDto.mismatchedRecordSummaries;
              console.log("mismatched field summaries:", currentField);
              this.scrollIntoSelectedRecord();
            }
          }
        }
      }
    });
  }


  onCloseWorksheetField(worksheetName: any, mismatchField: DiagnosticReportFieldModelDto) {
    const currentSelectedWorksheet = this.diagnosticReportWorksheetModels.find(worksheet => worksheet.worksheetName === worksheetName);
    if (currentSelectedWorksheet) {
      const fieldModelDto = currentSelectedWorksheet.diagnosticReportFieldModelsDto.find(o => o.columnIndex === mismatchField.columnIndex);
      fieldModelDto.mismatchedRecordSummaries = new Array<DiagnosticReportMismatchedRecordSummaryModel>();
    }
  }

  onAfterExpand(worksheetName: any, mismatchField: DiagnosticReportFieldModelDto) {
  }
}
